import React, { useState } from "react";
import { Constants } from "@eagerdog/constants";
import { useLocation, useNavigate } from "react-router-dom";
import { IFeedPostItem } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { helperService } from "src/services/helper.service";
import { toast } from "src/components/Toast/ToastManager";
import ContextMenu, { IContextMenuOption } from "src/components/ContextMenu/ContextMenu";
import Confirm from "src/components/Confirm/Confirm";
import { Modal, useModal } from "src/components/Modal/Modal";

import ReportPostModal from "./ReportPostModal";
import EditPostModal from "./EditPostModal";
import styles from "./FeedCard.module.scss";

interface IProps {
    post: IFeedPostItem;
    /** Feed reload after delete / report. */
    onChange: () => void;
    /** Collapse this card immediately after a successful report. */
    onReported: () => void;
    /** Canonical permalink for "Copy link". */
    shareUrl: string;
    /** Card-specific entries (e.g. Unfollow on club posts), shown between Copy link and Delete/Report. */
    extraOptions?: IContextMenuOption[];
}

/**
 * Feed cards render on public pages (a public dog's Activity tab), so Like,
 * Share, and Report send a logged-out viewer to log in and come back here.
 * Returns true when the viewer is signed in and the action may proceed.
 */
export const useRequireLogin = (): (() => boolean) => {
    const navigate = useNavigate();
    const location = useLocation();
    return () => {
        if (helperService.isLoggedIn()) return true;
        navigate(`/login?r=${encodeURIComponent(location.pathname + location.search)}`);
        return false;
    };
};

/**
 * The "⋯" menu on every feed card. Authors (and club managers on club
 * posts) get Edit (when the post has editable text) and Delete; everyone
 * else gets Report. Owns the confirm, edit and report modals so both card
 * types share one implementation.
 */
const PostCardMenu: React.FC<IProps> = ({ post, onChange, onReported, shareUrl, extraOptions = [] }) => {
    const deleteModal = useModal();
    const editModal = useModal();
    const reportModal = useModal();
    const requireLogin = useRequireLogin();
    const [deleting, setDeleting] = useState(false);

    const confirmDelete = async () => {
        setDeleting(true);
        try {
            if (post.author_type === Constants.post_author_type.club) {
                await apiService.deleteClubPost(post.author_id, post._id);
            } else {
                await apiService.deletePost(post._id);
            }
            deleteModal.toggle();
            onChange();
        } catch {
            toast.show({
                title: "Couldn't delete post",
                content: "Please try again.",
                duration: 6000,
                type: "fail",
            });
        } finally {
            setDeleting(false);
        }
    };

    // Copy link works logged out too (no login round-trip for a URL); the
    // share is only counted when we know who shared.
    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.show({ title: "Link copied", content: shareUrl, duration: 4000, type: "success" });
            if (helperService.isLoggedIn()) {
                apiService.recordPostShare(post._id, Constants.share_channel.copy_link).catch(() => {});
            }
        } catch {
            toast.show({ title: "Couldn't copy link", content: shareUrl, duration: 6000, type: "fail" });
        }
    };

    const options: IContextMenuOption[] = [
        { label: "Copy link", onClick: copyLink },
        ...extraOptions,
        ...(post.viewer_can_edit ? [{ label: "Edit post", onClick: editModal.toggle }] : []),
        post.viewer_can_delete
            ? { label: "Delete post", onClick: deleteModal.toggle }
            : { label: "Report post", onClick: () => { if (requireLogin()) reportModal.toggle(); } },
    ];

    return (
        <>
            <div className={styles.menu}>
                <ContextMenu options={options} />
            </div>
            <Modal
                className="Confirmation"
                isShown={deleteModal.isShown}
                hide={deleteModal.toggle}
                modalContent={
                    <Confirm
                        message={
                            post.author_type === Constants.post_author_type.club
                                ? "This removes the update from everyone who follows the club."
                                : "This removes the post from your feed and from anyone who follows this dog."
                        }
                        onConfirm={confirmDelete}
                        onCancel={deleteModal.toggle}
                        submitting={deleting}
                    />
                }
            />
            <Modal
                className="reportPostModal"
                isShown={editModal.isShown}
                hide={editModal.toggle}
                modalContent={
                    <EditPostModal
                        post={post}
                        onClose={editModal.toggle}
                        onSaved={onChange}
                    />
                }
            />
            <Modal
                className="reportPostModal"
                isShown={reportModal.isShown}
                hide={reportModal.toggle}
                modalContent={
                    <ReportPostModal
                        postId={post._id}
                        onClose={reportModal.toggle}
                        onReported={onReported}
                    />
                }
            />
        </>
    );
};

export default PostCardMenu;

/** "· Edited" after the timestamp once the author has changed the text. */
export const EditedMarker: React.FC<{ post: IFeedPostItem }> = ({ post }) =>
    post.edited_at ? <span title={`Edited ${new Date(post.edited_at).toLocaleString()}`}>· Edited</span> : null;

/** Header chip shown to the author while their post is hidden pending review. */
export const UnderReviewChip: React.FC = () => (
    <span className={styles.reviewChip} title="Hidden from others while we take a look.">
        Under review
    </span>
);

/** What a card collapses to right after the viewer reports it. */
export const ReportedPlaceholder: React.FC = () => (
    <div className={`${styles.FeedCard} ${styles.reportedPlaceholder}`}>
        You reported this post.
    </div>
);
